import {create} from 'zustand';
import {
    User,
    MessageType,
    WebSendTokenMessage,
    WebRequestTokenMessage,
    WebLogoutMessage,
    PushTokenResultMessage,
} from './types';
import {useNotiStore} from './noti';
import {updateNotiSetting} from '../services/notiSettingService';
import {
    saveTokens,
    loadTokens,
    removeTokens,
    savePushTokenStatus,
    loadPushTokenStatus,
    removePushTokenStatus,
} from '../utils/storage';
import {requests} from '../apis/wrapperAPI';
import {StatusCodes} from 'http-status-codes';
import {validateAndHandleUserStatus} from '../utils/userStatusHandler';
import {useChatStore} from './chat';
import {DeviceTypeType} from 'constants/platformType';

interface UserState {
    user: User | null;
    isLoggedIn: boolean;
    accessToken: string;
    refreshToken: string;

    setUser: (user: User | null) => void;
    setAccessToken: (accessToken: string) => void;
    setRefreshToken: (refreshToken: string) => void;
    setField: <K extends keyof UserState>(field: K, value: UserState[K]) => void;

    sendTokensToApp: (accessToken: string, refreshToken: string) => void;
    requestTokensFromApp: () => void;

    login: (user: User, accessToken: string, refreshToken: string) => Promise<void>;
    logout: () => Promise<void>;
    initialize: () => Promise<void>;

    // 푸시알림 토큰 관련
    isPushTokenSaved: () => boolean;
    markPushTokenAsSaved: () => void;
    clearPushTokenSavedStatus: () => void;
    requestPushTokenFromRN: () => void;
    savePushTokenToServer: (token: string, deviceType: DeviceTypeType) => Promise<boolean>;
    managePushToken: () => void;
    handlePushTokenFromRN: (token: string, deviceType: DeviceTypeType) => Promise<void>;
    sendPushTokenResultToApp: (success: boolean, token: string, deviceType: DeviceTypeType, message?: string) => void;
}

const initialState = {
    user: null,
    isLoggedIn: false,
    accessToken: '',
    refreshToken: '',
};

export const useUserStore = create<UserState>((set, get) => ({
    ...initialState,
    setUser: user =>
        set({
            user,
            isLoggedIn: !!user,
        }),
    setAccessToken: accessToken => set({accessToken}),
    setRefreshToken: refreshToken => set({refreshToken}),
    setField: (field, value) => set(state => ({...state, [field]: value})),

    // 웹뷰 -> 앱으로 토큰 전송 (로그인 성공 시, 토큰 갱신 시)
    sendTokensToApp: (accessToken: string, refreshToken: string) => {
        if (window.ReactNativeWebView) {
            const message: WebSendTokenMessage = {
                type: MessageType.WEB_SEND_TOKEN,
                payload: {accessToken, refreshToken},
            };
            window.ReactNativeWebView.postMessage(JSON.stringify(message));
            console.log('웹뷰 -> 앱: 토큰 전송 성공', {accessToken, refreshToken});
        } else {
            console.warn('앱 환경이 아니어서 토큰을 전송할 수 없습니다.');
        }
    },

    // 웹뷰 -> 앱에게 토큰을 요청
    requestTokensFromApp: () => {
        if (window.ReactNativeWebView) {
            const message: WebRequestTokenMessage = {
                type: MessageType.WEB_REQUEST_TOKEN,
            };
            window.ReactNativeWebView.postMessage(JSON.stringify(message));
            console.log('웹뷰 -> 앱: 토큰 요청');
        } else {
            console.warn('앱 환경이 아니어서 앱에게 토큰을 요청할 수 없습니다.');
        }
    },

    login: async (user, accessToken, refreshToken) => {
        if (accessToken && refreshToken) {
            await saveTokens({accessToken, refreshToken});
            // 로그인 성공 시 앱으로 토큰 전달 추가
            get().sendTokensToApp(accessToken, refreshToken);
        }
        set({
            user,
            accessToken,
            refreshToken,
            isLoggedIn: true,
        });
    },
    logout: async () => {
        await removeTokens();
        set({
            ...initialState,
            isLoggedIn: false,
        });

        // 채팅 상태 초기화
        const {resetChatState} = useChatStore.getState();
        resetChatState();

        if (window.ReactNativeWebView) {
            const message: WebLogoutMessage = {
                type: MessageType.WEB_LOGOUT,
            };
            window.ReactNativeWebView.postMessage(JSON.stringify(message));
            console.log('웹뷰 -> 앱: 로그아웃');
        }
    },

    // 푸시알림 토큰 관련 함수들
    isPushTokenSaved: (): boolean => {
        return loadPushTokenStatus();
    },

    markPushTokenAsSaved: (): void => {
        savePushTokenStatus();
    },

    clearPushTokenSavedStatus: (): void => {
        removePushTokenStatus();
    },

    requestPushTokenFromRN: (): void => {
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: MessageType.GET_NOTIFICATION_TOKEN,
                }),
            );
        } else {
            console.warn('앱 환경이 아니어서 푸시알림 토큰을 요청할 수 없습니다.');
        }
    },

    sendPushTokenResultToApp: (success: boolean, token: string, deviceType: DeviceTypeType, message?: string) => {
        if (window.ReactNativeWebView) {
            const resultMessage: PushTokenResultMessage = {
                type: MessageType.GET_NOTIFICATION_TOKEN_RESULT,
                payload: {
                    success,
                    token,
                    deviceType,
                    ...(message && {message}),
                    timestamp: new Date().toISOString(),
                },
            };
            window.ReactNativeWebView.postMessage(JSON.stringify(resultMessage));
            console.log('웹뷰 -> 앱: 푸시알림 토큰 전송 결과 전송', resultMessage.payload);
        } else {
            console.warn('앱 환경이 아니어서 푸시알림 토큰 결과를 전송할 수 없습니다.');
        }
    },

    savePushTokenToServer: async (token: string, deviceType: DeviceTypeType): Promise<boolean> => {
        try {
            const response = await requests.post('/api/users/token', true, {
                token,
                deviceType,
            });
            if (response.isSuccess) {
                get().markPushTokenAsSaved();

                get().sendPushTokenResultToApp(true, response.result.token, response.result.deviceType);
                return true;
            } else {
                console.error('푸시알림 토큰 서버 저장 실패:', response.message);
                get().sendPushTokenResultToApp(
                    false,
                    response.result.token,
                    response.result.deviceType,
                    response.message,
                );
                return false;
            }
        } catch (error) {
            console.error('푸시알림 토큰 서버 저장 중 오류:', error);
            return false;
        }
    },

    managePushToken: (): void => {
        // 로그인할 때마다 새로운 푸시알림 토큰을 요청
        console.log('로그인 시 새로운 푸시알림 토큰을 요청합니다.');
        get().requestPushTokenFromRN();
    },

    handlePushTokenFromRN: async (token: string, deviceType: DeviceTypeType): Promise<void> => {
        console.log('RN에서 푸시알림 토큰 수신:', token, 'deviceType:', deviceType);

        const success = await get().savePushTokenToServer(token, deviceType);
        if (success) {
            console.log('푸시알림 토큰 처리 완료');
        } else {
            console.error('푸시알림 토큰 처리 실패');
        }
    },

    initialize: async () => {
        // URL 파라미터로 테스트 토큰 확인 (테스트 승인용)
        // 이후 이 부분 부터 ----->
        const urlParams = new URLSearchParams(window.location.search);
        const testAccessToken = urlParams.get('test_access_token');
        const testRefreshToken = urlParams.get('test_refresh_token');

        if (testAccessToken && testRefreshToken) {
            try {
                await saveTokens({accessToken: testAccessToken, refreshToken: testRefreshToken});

                const {isSuccess, result, code} = await requests.get('/api/users/me', true);

                if (isSuccess && result) {
                    const isValidUser = await validateAndHandleUserStatus(result, undefined);

                    if (isValidUser) {
                        await get().login(result, testAccessToken, testRefreshToken);

                        // URL에서 테스트 파라미터 제거
                        const newUrl = new URL(window.location.href);
                        newUrl.searchParams.delete('test_access_token');
                        newUrl.searchParams.delete('test_refresh_token');
                        window.history.replaceState({}, '', newUrl.toString());

                        console.log('테스트 토큰으로 로그인 완료');
                        return;
                    } else {
                        console.log('테스트 토큰 사용자 상태가 비정상입니다.');
                        set({...initialState});
                    }
                } else {
                    console.error(`테스트 토큰으로 사용자 정보 조회 실패 (코드: ${code})`);
                    set({...initialState});
                }
            } catch (error) {
                console.error('테스트 토큰 로그인 실패:', error);
                set({...initialState});
            }
        }
        // <----- 이부분까지 삭제 필요

        // 앱에서 보내는 메시지를 수신하는 핸들러 등록
        const handleAuthMessage = async (event: MessageEvent) => {
            try {
                // 알림 권한 메시지가 스킵되지 않도록 처리
                if (typeof event.data === 'string' && event.data.includes('UPDATE_NOTIFICATION_PERMISSION_RESULT')) {
                    const data = JSON.parse(event.data);
                    if (data.type === MessageType.UPDATE_NOTIFICATION_PERMISSION_RESULT) {
                        const permissionStatus = data.data; // "enabled" | "disabled"
                        console.log('알림 권한 결과:', permissionStatus);

                        if (permissionStatus === 'enabled' || permissionStatus === 'disabled') {
                            const newNotificationYn = permissionStatus === 'enabled';

                            // 서버 업데이트 및 전역 상태 변경
                            const handleNotificationUpdate = async () => {
                                try {
                                    const {setNotificationYn, setLoading} = useNotiStore.getState();
                                    setLoading(true);

                                    const result = await updateNotiSetting(newNotificationYn);
                                    console.log('알림 설정 업데이트 성공:', result);

                                    setNotificationYn(result);
                                } catch (error) {
                                    console.error('알림 설정 업데이트 실패:', error);
                                } finally {
                                    const {setLoading} = useNotiStore.getState();
                                    setLoading(false);
                                }
                            };

                            handleNotificationUpdate();
                        }
                    }

                    return;
                }

                const data = JSON.parse(event.data);

                if (data.type === MessageType.APP_SEND_TOKEN) {
                    console.log('앱 -> 웹뷰: 토큰 수신', data.payload);
                    const {accessToken, refreshToken} = data.payload;

                    if (!accessToken || !refreshToken) {
                        console.warn('앱에서 유효하지 않은 토큰을 받았습니다.');
                        return;
                    }

                    await saveTokens({accessToken, refreshToken});
                    set({accessToken, refreshToken});

                    try {
                        const {isSuccess, result} = await requests.get('/api/users/me', true);
                        if (isSuccess && result) {
                            // 앱에서 토큰을 받은 경우에는 네비게이션 없이 상태만 확인
                            const isValidUser = await validateAndHandleUserStatus(result, undefined);
                            console.log('isValidUser 결과:', isValidUser);
                            if (isValidUser) {
                                set({
                                    user: result,
                                    isLoggedIn: true,
                                });
                            } else {
                                console.log('사용자 상태가 비정상입니다. 로그아웃 처리됨.');
                            }
                        } else {
                            await get().logout();
                            console.log('세션이 만료되었습니다. 다시 로그인해주세요.');
                        }
                    } catch (apiError) {
                        await get().logout();
                        console.log('네트워크 오류 또는 세션 만료. 다시 로그인해주세요.');
                    }
                } else if (data.type === MessageType.GET_NOTIFICATION_TOKEN_CALLBACK && data.token && data.deviceType) {
                    // 푸시 토큰 콜백 메시지 처리
                    console.log('RN -> 웹뷰: 푸시 토큰 수신', data);
                    await get().handlePushTokenFromRN(data.token, data.deviceType);
                }
            } catch (error) {
                console.error('인증 메시지 파싱 오류:', error);
            }
        };

        const handleMessageWrapper = (event: Event) => {
            handleAuthMessage(event as MessageEvent);
        };

        if (window.ReactNativeWebView && !window.authMessageListenerRegistered) {
            window.addEventListener('message', handleAuthMessage);
            document.addEventListener('message', handleMessageWrapper);
            window.authMessageListenerRegistered = true;
            console.log('인증 이벤트 리스너 등록 완료');
        } else if (window.ReactNativeWebView && window.authMessageListenerRegistered) {
            console.log('인증 이벤트 리스너가 이미 등록되어 있습니다.');
        } else {
            console.log('앱 환경이 아니어서 인증 이벤트 리스너를 등록할 수 없습니다.');
        }

        // 웹뷰 스토리지에서 토큰 로드 시도
        const tokens = await loadTokens();
        if (!tokens || !tokens.accessToken || !tokens.refreshToken) {
            console.log('로컬 스토리지에 유효한 토큰이 없어 초기 상태로 설정합니다.');
            set({...initialState});
            return;
        }

        set({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
        });

        if (window.ReactNativeWebView) {
            get().sendTokensToApp(tokens.accessToken, tokens.refreshToken);
        }

        try {
            const {isSuccess, result, code} = await requests.get('/api/users/me', true);
            if (isSuccess && result) {
                // 초기화 시에는 네비게이션 x
                const isValidUser = await validateAndHandleUserStatus(result, undefined);
                if (isValidUser) {
                    set({
                        user: result,
                        isLoggedIn: true,
                    });
                } else {
                    console.log('사용자 상태가 비정상입니다. 로그아웃 처리됨.');
                }
            } else {
                if (code === StatusCodes.UNAUTHORIZED) {
                    console.error('인증 실패(401) 발생. 토큰 재발급 실패 또는 유효하지 않은 토큰. 로그아웃 처리.');
                    await removeTokens();
                    set({...initialState});
                } else {
                    console.error(
                        `유저 정보 조회 실패 (코드: ${code}, 메시지: ${result?.message || '알 수 없음'}). 토큰 유지.`,
                    );
                }
            }
        } catch (error) {
            console.error('유저 정보 조회 또는 토큰 재발급 중 심각한 오류 발생. 로그아웃 처리.', error);
            await removeTokens();
            set({...initialState});
        }
    },
}));

// 사용 예시:
// const { user, accessToken, refreshToken, login, logout, isLoggedIn } = useUserStore();
// await login(userData, 'accessToken', 'refreshToken');
// await logout();
